/**
 * MeshChatStub replaces the standard `TextAreaChat` for mesh-mode
 * conversations.
 *
 * Mesh artifacts produced by Tripo are sculpted (not parametric) and so
 * cannot be edited iteratively. The user's only chat-side action is
 * "Regenerate", which re-runs the source image through the same pipeline.
 * The button is disabled while a job is already in flight (`pendingMeshId`
 * set) and surfaces failures via a toast.
 *
 * In addition to Regenerate, this stub exposes three Download buttons —
 * GLB / STL / OBJ — for the current mesh. GLB is the raw Tripo output
 * fetched directly from Storage; STL and OBJ are converted client-side
 * via three.js exporters (see `@/lib/meshExport`).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDownloadURL, ref } from 'firebase/storage';
import type { Conversation } from '@shared/types';
import { startMeshFromImage } from '@/services/meshGenerationService';
import { useToast } from '@/hooks/use-toast';
import { storage } from '@/lib/firebase';
import { downloadGlb, downloadStl, downloadObj } from '@/lib/meshExport';

interface Props {
  conversation: Conversation;
}

type DownloadFormat = 'glb' | 'stl' | 'obj';

export function MeshChatStub({ conversation }: Props) {
  const { t } = useTranslation('common');
  const { toast } = useToast();
  const inFlight = !!conversation.pendingMeshId;
  const canRegenerate =
    !inFlight && typeof conversation.sourceImageId === 'string';

  // Resolve the GLB download URL once whenever `currentMeshPath`
  // changes; all three download buttons share it.
  const meshPath = conversation.currentMeshPath;
  const [meshUrl, setMeshUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!meshPath) {
      setMeshUrl(null);
      return;
    }
    let cancelled = false;
    void getDownloadURL(ref(storage, meshPath))
      .then((url) => {
        if (!cancelled) setMeshUrl(url);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[MeshChatStub] getDownloadURL failed:', err);
          setMeshUrl(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [meshPath]);

  const [isDownloading, setIsDownloading] = useState<DownloadFormat | null>(
    null,
  );
  const canDownload = !inFlight && !!meshUrl && !!conversation.currentMeshId;

  const handleRegenerate = async () => {
    if (!canRegenerate || !conversation.sourceImageId) return;
    try {
      await startMeshFromImage({
        conversationId: conversation.id,
        imageId: conversation.sourceImageId,
      });
    } catch (err) {
      toast({
        title: t('meshChat.regenerateFailedTitle', {
          defaultValue: "Couldn't start regeneration",
        }),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  };

  const handleDownload = async (format: DownloadFormat) => {
    if (!canDownload || !meshUrl || !conversation.currentMeshId) return;
    setIsDownloading(format);
    const filename = `mesh-${conversation.currentMeshId}.${format}`;
    try {
      if (format === 'glb') {
        await downloadGlb(meshUrl, filename);
      } else if (format === 'stl') {
        await downloadStl(meshUrl, filename);
      } else {
        await downloadObj(meshUrl, filename);
      }
    } catch (err) {
      toast({
        title: t('meshChat.downloadFailedTitle', {
          format: format.toUpperCase(),
          defaultValue: "Couldn't download {{format}}",
        }),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(null);
    }
  };

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-synergy-bg-dark p-6 text-center">
      <p className="max-w-md text-sm text-k-ink/80">
        {t('meshChat.intro', {
          defaultValue:
            "Mesh models are sculpted, not parametric. Editing isn't supported in this mode. Re-generate for a different result, or start a new conversation with a text description for a parametric model.",
        })}
      </p>
      <div className="flex flex-row items-center gap-2">
        {(['glb', 'stl', 'obj'] as const).map((fmt) => (
          <DownloadButton
            key={fmt}
            format={fmt}
            disabled={!canDownload || isDownloading !== null}
            busy={isDownloading === fmt}
            onClick={() => void handleDownload(fmt)}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={() => void handleRegenerate()}
        disabled={!canRegenerate}
        className="inline-flex items-center gap-2 rounded-full border-2 border-brand-indigo bg-brand-indigo px-4 py-2 text-xs font-bold text-brand-cream hover:bg-brand-indigo-hover disabled:cursor-not-allowed disabled:opacity-60"
      >
        {t('meshChat.regenerateButton', { defaultValue: 'Regenerate' })}
      </button>
    </div>
  );
}

interface DownloadButtonProps {
  format: DownloadFormat;
  disabled: boolean;
  busy: boolean;
  onClick: () => void;
}

function DownloadButton({
  format,
  disabled,
  busy,
  onClick,
}: DownloadButtonProps) {
  const { t } = useTranslation('common');
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={t('meshChat.downloadAria', {
        format: format.toUpperCase(),
        defaultValue: 'Download {{format}}',
      })}
      className="inline-flex items-center gap-2 rounded-full border-2 border-brand-indigo bg-brand-indigo px-4 py-2 text-xs font-bold text-brand-cream hover:bg-brand-indigo-hover disabled:cursor-not-allowed disabled:opacity-60"
    >
      {busy && (
        <span
          aria-hidden="true"
          className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-brand-cream border-t-transparent"
        />
      )}
      {format.toUpperCase()}
    </button>
  );
}

export default MeshChatStub;
